import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProviderAuth } from '@/context/ProviderAuthContext';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import PasscodeInput from '@/components/PasscodeInput';
import PhoneInput from '@/components/PhoneInput';
import StateSelect from '@/components/StateSelect';
import { User, Mail, Phone, Building2, Save, Shield, Lock, Key, AlertCircle, Wrench } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cleanPhone, formatPhoneDisplay } from '@/utils/phoneUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderSettings = () => {
  const navigate = useNavigate();
  const { provider, token, setProvider } = useProviderAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [allServiceTypes, setAllServiceTypes] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState(provider?.service_types || []);
  const [isSavingServiceTypes, setIsSavingServiceTypes] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'editProfile' or 'saveServiceTypes'
  const [formData, setFormData] = useState({
    full_name: provider?.full_name || '',
    company_name: provider?.company_name || '',
    phone: provider?.phone || '',
    city: provider?.city || '',
    state: provider?.state || '',
    notes: provider?.notes || ''
  });

  // Security verification states
  const [showPasscodeSetup, setShowPasscodeSetup] = useState(false);
  const [showPasscodeChange, setShowPasscodeChange] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showPasscodeVerify, setShowPasscodeVerify] = useState(false);
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const [verifyPasscode, setVerifyPasscode] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [currentPasscode, setCurrentPasscode] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showVerifyPasswordText, setShowVerifyPasswordText] = useState(false);

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (provider) {
      setFormData({
        full_name: provider.full_name || '',
        company_name: provider.company_name || '',
        phone: provider.phone || '',
        city: provider.city || '',
        state: provider.state || '',
        notes: provider.notes || ''
      });
      setSelectedServiceTypes(provider.service_types || []);
      
      if (!fetchedRef.current) {
        fetchedRef.current = true;
        fetchServiceTypes();
      }
    }
  }, [provider]);

  const fetchServiceTypes = async () => {
    try {
      const response = await axios.get(`${API}/public/service-types`);
      if (response.data) {
        setAllServiceTypes(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch service types:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = typeof e === 'string' ? e : e.target.value;
    setFormData(prev => ({ ...prev, phone: value }));
  };

  const handleSaveProfile = async () => {
    if (!formData.full_name || !formData.company_name || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      const cleanedData = {
        ...formData,
        phone: cleanPhone(formData.phone)
      };

      const response = await axios.put(`${API}/provider/profile`, cleanedData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProvider(response.data);
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const requireVerification = (action) => {
    // Verification disabled for now — execute action directly
    setPendingAction(action);
    if (action === 'editProfile') {
      setIsEditing(true);
    } else if (action === 'saveServiceTypes') {
      doSaveServiceTypes();
    }
  };

  const executePendingAction = async (action) => {
    if (action === 'editProfile') {
      setIsEditing(true);
    } else if (action === 'saveServiceTypes') {
      await doSaveServiceTypes();
    }
    setPendingAction(null);
  };

  const handleEditProfile = () => {
    requireVerification('editProfile');
  };

  const handlePasscodeVerified = async () => {
    if (verifyPasscode.length !== 6) {
      toast.error('Please enter your 6-digit passcode');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/provider/passcode/verify`,
        { passcode: verifyPasscode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setShowPasscodeVerify(false);
        setVerifyPasscode('');
        const action = pendingAction;
        toast.success('Passcode verified!');
        await executePendingAction(action);
      }
    } catch (error) {
      console.error('Passcode verification failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid passcode');
      setVerifyPasscode('');
    }
  };

  const handlePasswordVerified = async () => {
    if (!verifyPassword) {
      toast.error('Please enter your password');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/provider/password/verify`,
        { password: verifyPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setShowPasswordVerify(false);
        setVerifyPassword('');
        setShowVerifyPasswordText(false);
        const action = pendingAction;
        toast.success('Password verified!');
        await executePendingAction(action);
      }
    } catch (error) {
      console.error('Password verification failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid password');
      setVerifyPassword('');
    }
  };

  const handleSetupPasscode = async () => {
    if (newPasscode.length !== 6 || confirmPasscode.length !== 6) {
      toast.error('Passcode must be exactly 6 digits');
      return;
    }

    if (newPasscode !== confirmPasscode) {
      toast.error('Passcodes do not match');
      return;
    }

    try {
      await axios.post(
        `${API}/provider/passcode/setup`,
        { passcode: newPasscode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowPasscodeSetup(false);
      setNewPasscode('');
      setConfirmPasscode('');
      setProvider({ ...provider, has_passcode: true });
      toast.success('Passcode created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set passcode');
    }
  };

  const handleChangePasscode = async () => {
    if (currentPasscode.length !== 6) {
      toast.error('Please enter your current 6-digit passcode');
      return;
    }

    if (newPasscode.length !== 6 || confirmPasscode.length !== 6) {
      toast.error('New passcode must be exactly 6 digits');
      return;
    }

    if (newPasscode !== confirmPasscode) {
      toast.error('New passcodes do not match');
      return;
    }

    try {
      await axios.put(
        `${API}/provider/passcode/change`,
        { current_passcode: currentPasscode, new_passcode: newPasscode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowPasscodeChange(false);
      setCurrentPasscode('');
      setNewPasscode('');
      setConfirmPasscode('');
      toast.success('Passcode changed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change passcode');
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      await axios.put(
        `${API}/provider/password/change`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleToggleServiceType = (type) => {
    setSelectedServiceTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleSaveServiceTypes = async () => {
    if (selectedServiceTypes.length === 0) {
      toast.error('Please select at least one service type');
      return;
    }
    requireVerification('saveServiceTypes');
  };

  const doSaveServiceTypes = async () => {
    setIsSavingServiceTypes(true);
    try {
      const response = await axios.put(
        `${API}/provider/profile`,
        { service_types: selectedServiceTypes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProvider(response.data);
      toast.success('Service types updated successfully!');
    } catch (error) {
      console.error('Failed to update service types:', error);
      toast.error(error.response?.data?.detail || 'Failed to update service types');
    } finally {
      setIsSavingServiceTypes(false);
    }
  };

  if (!provider) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-stone-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        <div className="space-y-6">
          {/* Profile Information */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-stone-900">Profile Information</h2>
                <p className="text-sm text-stone-600">Update your personal and business details</p>
              </div>
              {!isEditing ? (
                <Button onClick={handleEditProfile} variant="outline">
                  {provider?.has_passcode && <Shield size={16} className="mr-2" />}
                  Edit Profile
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={() => setIsEditing(false)} variant="outline">
                    Cancel
                  </Button>
                  <Button onClick={handleSaveProfile} disabled={isSaving}>
                    <Save size={16} className="mr-2" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div>
                <Label htmlFor="full_name" className="flex items-center gap-2 mb-2">
                  <User size={16} />
                  Full Name *
                </Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="John Doe"
                />
              </div>

              {/* Company Name */}
              <div>
                <Label htmlFor="company_name" className="flex items-center gap-2 mb-2">
                  <Building2 size={16} />
                  Company Name *
                </Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="ABC Services LLC"
                />
              </div>

              {/* Email (Read-only) */}
              <div>
                <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                  <Mail size={16} />
                  Email
                </Label>
                <Input
                  id="email"
                  value={provider.email}
                  disabled
                  className="bg-stone-100"
                />
                <p className="text-xs text-stone-500 mt-1">Email cannot be changed</p>
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2 mb-2">
                  <Phone size={16} />
                  Phone Number *
                </Label>
                {isEditing ? (
                  <PhoneInput
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    disabled={!isEditing}
                  />
                ) : (
                  <Input
                    type="tel"
                    value={formatPhoneDisplay(formData.phone)}
                    disabled
                    className="bg-stone-50 cursor-not-allowed"
                  />
                )}
              </div>

              {/* City */}
              <div>
                <Label htmlFor="city" className="mb-2 block">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="New York"
                />
              </div>

              {/* State */}
              <div>
                <Label htmlFor="state" className="mb-2 block">State</Label>
                <StateSelect
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Select State"
                />
              </div>

              {/* Service Type (Read-only) */}
              <div className="md:col-span-2">
                <Label className="mb-2 block">Service Type</Label>
                <div className="flex flex-wrap gap-1">
                  {(provider.service_types && provider.service_types.length > 0) ? provider.service_types.map((t) => (
                    <span key={t} className="px-2 py-1 bg-[#1A4D2E]/10 text-[#1A4D2E] rounded-full text-xs font-medium">{t}</span>
                  )) : (
                    <span className="text-sm text-stone-500">{provider.service_type || 'Not set'}</span>
                  )}
                </div>
                <p className="text-xs text-stone-500 mt-1">Manage service types in the section below</p>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <Label htmlFor="notes" className="mb-2 block">Notes</Label>
                <Input
                  id="notes"
                  name="notes"
                  value={formData.notes || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Additional information..."
                />
              </div>
            </div>
          </Card>

          {/* Service Types Management */}
          <Card className="p-6" data-testid="service-types-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2">
                  <Wrench size={20} />
                  Service Types
                </h2>
                <p className="text-sm text-stone-600">Select the services you provide</p>
              </div>
              <Button
                onClick={handleSaveServiceTypes}
                disabled={isSavingServiceTypes}
                className="bg-[#1A4D2E] hover:bg-[#143D24]"
                data-testid="save-service-types-btn"
              >
                <Save size={16} className="mr-2" />
                {isSavingServiceTypes ? 'Saving...' : 'Save'}
              </Button>
            </div>

            {allServiceTypes.length > 0 ? (
              <>
                <div className="border border-stone-200 rounded-lg p-3 max-h-60 overflow-y-auto bg-white">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
                    {allServiceTypes.map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-2 cursor-pointer hover:bg-stone-50 p-2 rounded text-sm"
                        data-testid={`service-type-option-${type.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedServiceTypes.includes(type)}
                          onChange={() => handleToggleServiceType(type)}
                          className="w-4 h-4 text-[#1A4D2E] border-stone-300 rounded"
                        />
                        <span className="text-stone-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {selectedServiceTypes.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {selectedServiceTypes.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#1A4D2E]/10 text-[#1A4D2E] rounded-full text-xs font-medium"
                      >
                        {t}
                        <button
                          type="button"
                          onClick={() => handleToggleServiceType(t)}
                          className="hover:text-red-600 ml-0.5"
                          data-testid={`remove-service-type-${t.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-stone-500">Loading service types...</p>
            )}
          </Card>

          {/* Security Settings */}
          <Card className="p-6">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2">
                <Shield size={20} />
                Security Settings
              </h2>
              <p className="text-sm text-stone-600">Manage your password and passcode</p>
            </div>

            <div className="space-y-4">
              {/* Passcode Section */}
              <div className="flex items-center justify-between p-4 border border-stone-200 rounded-lg">
                <div>
                  <div className="font-medium flex items-center gap-2">
                    <Key size={18} />
                    6-Digit Passcode
                  </div>
                  <p className="text-sm text-stone-600">
                    {provider.has_passcode
                      ? 'Quick login with a 6-digit code'
                      : 'Set up a passcode for quick login'}
                  </p>
                </div>
                {provider.has_passcode ? (
                  <Button onClick={() => setShowPasscodeChange(true)} variant="outline">
                    Change Passcode
                  </Button>
                ) : (
                  <Button onClick={() => setShowPasscodeSetup(true)}>
                    Create Passcode
                  </Button>
                )}
              </div>

              {/* Password Section */}
              <div className="flex items-center justify-between p-4 border border-stone-200 rounded-lg">
                <div>
                  <div className="font-medium flex items-center gap-2">
                    <Lock size={18} />
                    Password
                  </div>
                  <p className="text-sm text-stone-600">Change your account password</p>
                </div>
                <Button onClick={() => setShowPasswordChange(true)} variant="outline">
                  Change Password
                </Button>
              </div>
            </div>
          </Card>

          {/* Customer Reviews QR Code - Moved to Reviews page */}
        </div>

      {/* Passcode Setup Dialog */}
      <Dialog open={showPasscodeSetup} onOpenChange={setShowPasscodeSetup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Passcode</DialogTitle>
            <DialogDescription>
              Set up a 6-digit passcode for quick login
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">New Passcode</Label>
              <PasscodeInput
                length={6}
                value={newPasscode}
                onChange={setNewPasscode}
                placeholder="Enter 6-digit passcode"
              />
            </div>
            <div>
              <Label className="mb-2 block">Confirm Passcode</Label>
              <PasscodeInput
                length={6}
                value={confirmPasscode}
                onChange={setConfirmPasscode}
                placeholder="Re-enter passcode"
              />
            </div>
            <Button onClick={handleSetupPasscode} className="w-full">
              Create Passcode
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Passcode Change Dialog */}
      <Dialog open={showPasscodeChange} onOpenChange={setShowPasscodeChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Passcode</DialogTitle>
            <DialogDescription>
              Enter your current passcode and set a new one
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">Current Passcode</Label>
              <PasscodeInput
                length={6}
                value={currentPasscode}
                onChange={setCurrentPasscode}
                placeholder="Enter current passcode"
              />
            </div>
            <div>
              <Label className="mb-2 block">New Passcode</Label>
              <PasscodeInput
                length={6}
                value={newPasscode}
                onChange={setNewPasscode}
                placeholder="Enter new passcode"
              />
            </div>
            <div>
              <Label className="mb-2 block">Confirm New Passcode</Label>
              <PasscodeInput
                length={6}
                value={confirmPasscode}
                onChange={setConfirmPasscode}
                placeholder="Re-enter new passcode"
              />
            </div>
            <Button onClick={handleChangePasscode} className="w-full">
              Change Passcode
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and set a new one
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <Label className="mb-2 block">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
              />
            </div>
            <div>
              <Label className="mb-2 block">Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
              />
            </div>
            <Button onClick={handleChangePassword} className="w-full">
              Change Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Passcode Verification Dialog for Edit Profile */}
      <Dialog open={showPasscodeVerify} onOpenChange={setShowPasscodeVerify}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#1A4D2E]/10 rounded-lg">
                <Shield className="text-[#1A4D2E]" size={20} />
              </div>
              <DialogTitle>Verify Your Passcode</DialogTitle>
            </div>
            <DialogDescription>
              Enter your 6-digit passcode to edit your profile information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <PasscodeInput
                value={verifyPasscode}
                onChange={setVerifyPasscode}
                onComplete={handlePasscodeVerified}
              />
            </div>
            <p className="text-xs text-stone-500 text-center">
              This verification is required to protect your profile information
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPasscodeVerify(false);
                setVerifyPasscode('');
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePasscodeVerified}
              disabled={verifyPasscode.length !== 6}
              className="flex-1 bg-[#1A4D2E]"
            >
              Verify
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Verification Dialog */}
      <Dialog open={showPasswordVerify} onOpenChange={(open) => {
        setShowPasswordVerify(open);
        if (!open) {
          setVerifyPassword('');
          setShowVerifyPasswordText(false);
          setPendingAction(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#1A4D2E]/10 rounded-lg">
                <Lock className="text-[#1A4D2E]" size={20} />
              </div>
              <DialogTitle>Verify Your Password</DialogTitle>
            </div>
            <DialogDescription>
              Enter your password to make changes to your profile
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  type={showVerifyPasswordText ? 'text' : 'password'}
                  value={verifyPassword}
                  onChange={(e) => setVerifyPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pr-12"
                  data-testid="verify-password-input"
                  onKeyDown={(e) => e.key === 'Enter' && handlePasswordVerified()}
                />
                <button
                  type="button"
                  onClick={() => setShowVerifyPasswordText(!showVerifyPasswordText)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showVerifyPasswordText ? <span className="text-sm">Hide</span> : <span className="text-sm">Show</span>}
                </button>
              </div>
            </div>
            <p className="text-xs text-stone-500 text-center">
              This verification is required to protect your profile information
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPasswordVerify(false);
                setVerifyPassword('');
                setShowVerifyPasswordText(false);
                setPendingAction(null);
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordVerified}
              disabled={!verifyPassword}
              className="flex-1 bg-[#1A4D2E]"
              data-testid="verify-password-submit"
            >
              Verify
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProviderSettings;
